import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { CheckCircle2, Sparkles, Loader2, RefreshCw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { startWeeklyReview, completeWeeklyReview } from '@/api/weeklyReviews'
import { useCreateJournalEntry } from '@/hooks/useJournal'
import { useBuckets } from '@/hooks/useBuckets'
import { useAllocation } from '@/hooks/useAllocation'
import { useCoaching } from '@/hooks/useCoaching'
import { useAppSettings } from '@/hooks/useAppSettings'
import { LetterBody } from './LetterBody'
import { weekStartISO } from '@/lib/dates'
import { cn } from '@/lib/utils'
import type { AllocationResult, BucketAlloc } from '@/lib/allocation'
import type { Bucket, AiSummaryBody } from '@/types'
import type { CoachingMode, CoachingResponse } from '@/types/coaching'

const TOTAL_STEPS = 5

// ─── Step: Rank review ────────────────────────────────────────────────────────

function RankStep({ buckets }: { buckets: Bucket[] }) {
  const active = buckets.filter((b) => !b.isSystem && !b.archivedAt).sort((a, b) => a.rank - b.rank)

  if (active.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No life areas defined yet. Add some in the Detail view.
      </p>
    )
  }

  return (
    <ol className="space-y-2">
      {active.map((b, i) => (
        <li key={b.id} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground/50 tabular-nums w-4 text-right flex-shrink-0">
            {i + 1}
          </span>
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: b.color }}
          />
          <span className="text-sm">{b.name}</span>
          {b.intention && (
            <span className="text-xs text-muted-foreground truncate flex-1">{b.intention}</span>
          )}
        </li>
      ))}
    </ol>
  )
}

// ─── Step: Attention snapshot ─────────────────────────────────────────────────

function AttentionStep({
  allocation,
  bucketMap,
}: {
  allocation: AllocationResult | undefined
  bucketMap: Map<string, Bucket>
}) {
  if (!allocation || allocation.totalWeight === 0) {
    return (
      <p className="text-sm text-muted-foreground italic leading-relaxed">
        No cadence data yet. Add a cadence to your priorities in the Detail view to
        see allocation here.
      </p>
    )
  }

  const sorted = [...allocation.byBucket]
    .filter((b) => b.share > 0 || b.targetAlloc != null)
    .sort((a, b) => {
      const ra = bucketMap.get(a.bucketId)?.rank ?? 999
      const rb = bucketMap.get(b.bucketId)?.rank ?? 999
      return ra - rb
    })

  return (
    <div className="space-y-3">
      {sorted.map((b: BucketAlloc) => {
        const bucket = bucketMap.get(b.bucketId)
        if (!bucket) return null
        const pct = Math.round(b.share * 100)
        const targetPct = b.targetAlloc != null ? Math.round(b.targetAlloc * 100) : null
        const underInvested = b.gap != null && b.gap > 0.03

        return (
          <div key={b.bucketId}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: bucket.color }}
                />
                <span className="text-xs truncate">{bucket.name}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <span
                  className={cn(
                    'text-xs tabular-nums font-medium',
                    underInvested ? 'text-amber-600' : 'text-foreground',
                  )}
                >
                  {pct}%
                </span>
                {targetPct != null && (
                  <span className="text-[10px] text-muted-foreground">/ {targetPct}%</span>
                )}
              </div>
            </div>
            <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{ width: `${Math.min(100, pct)}%`, backgroundColor: bucket.color, opacity: 0.8 }}
              />
              {targetPct != null && (
                <div
                  className="absolute inset-y-0 w-0.5 bg-muted-foreground/50"
                  style={{ left: `${Math.min(100, targetPct)}%` }}
                />
              )}
            </div>
          </div>
        )
      })}
      <p className="text-[11px] text-muted-foreground pt-1">
        Last 30 days · bar = actual · line marker = target
      </p>
    </div>
  )
}

// ─── Step: Reflection ─────────────────────────────────────────────────────────

function ReflectionStep({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-3">
      <div className="px-3 py-2 bg-muted/40 rounded-md">
        <p className="text-xs text-muted-foreground italic leading-relaxed">
          What shifted this week? What deserves more attention next week?
        </p>
      </div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Write freely — this will be saved as a weekly review journal entry…"
        rows={6}
        className="resize-none text-sm font-mono"
        autoFocus
      />
    </div>
  )
}

// ─── Step: Insights (with AI coaching) ───────────────────────────────────────

function InsightsStep({
  allocation,
  bucketMap,
  mode,
  context,
  generate,
  isGenerating,
  result,
  error,
}: {
  allocation: AllocationResult | undefined
  bucketMap: Map<string, Bucket>
  mode: CoachingMode
  context: ReturnType<typeof useCoaching>['context']
  generate: ReturnType<typeof useCoaching>['generate']
  isGenerating: boolean
  result: CoachingResponse | undefined
  error: string | undefined
}) {
  const score = allocation?.alignmentScore
  const gaps = (allocation?.byBucket ?? [])
    .filter((b) => b.gap != null && b.gap > 0.05)
    .sort((a, b) => b.gap! - a.gap!)
    .slice(0, 3)

  return (
    <div className="space-y-4">
      {/* Alignment score */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Alignment score
        </p>
        {score != null ? (
          <div className="flex items-end gap-2">
            <span className="text-4xl font-light tabular-nums">
              {Math.round(score * 100)}
            </span>
            <span className="text-sm text-muted-foreground mb-1">/ 100</span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            Not enough data — add cadence to your priorities to calculate.
          </p>
        )}
      </div>

      {/* Notable gaps */}
      {gaps.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Under-invested
          </p>
          <ul className="space-y-1.5">
            {gaps.map((b) => {
              const bucket = bucketMap.get(b.bucketId)
              if (!bucket) return null
              return (
                <li key={b.bucketId} className="flex items-center gap-2 text-xs">
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: bucket.color }}
                  />
                  <span className="font-medium">{bucket.name}</span>
                  <span className="text-amber-600 tabular-nums">
                    {Math.round(b.share * 100)}% actual vs {Math.round((b.targetAlloc ?? 0) * 100)}% target
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* AI coaching letter */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Coaching letter
        </p>

        {!result && !isGenerating && !error && (
          <Button
            size="sm"
            onClick={() => void generate(mode)}
            disabled={!context}
            className="gap-1.5"
          >
            <Sparkles size={13} />
            Get coaching letter
          </Button>
        )}

        {isGenerating && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            Composing your letter…
          </div>
        )}

        {error && (
          <div className="space-y-2">
            <p className="text-xs text-destructive">{error}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void generate(mode)}
              disabled={!context || isGenerating}
              className="gap-1.5"
            >
              <RefreshCw size={12} />
              Try again
            </Button>
          </div>
        )}

        {result && !isGenerating && (
          <div className="space-y-4">
            <LetterBody
              body={result.body as AiSummaryBody}
              distressFloorTriggered={result.distressFloorTriggered}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void generate(mode)}
              disabled={!context || isGenerating}
              className="gap-1.5 text-muted-foreground h-7 text-xs"
            >
              <RefreshCw size={11} />
              Regenerate
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Step: Complete ───────────────────────────────────────────────────────────

function CompleteStep({
  periodStart,
  reflectionSaved,
  letterReceived,
  score,
}: {
  periodStart: string
  reflectionSaved: boolean
  letterReceived: boolean
  score: number | null | undefined
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-4 text-center">
      <CheckCircle2 size={48} className="text-primary/70" strokeWidth={1.5} />
      <div>
        <p className="text-base font-medium">
          Week of {format(new Date(periodStart + 'T12:00:00'), 'MMMM d')} — done
        </p>
        <p className="text-sm text-muted-foreground mt-1">Weekly review recorded.</p>
      </div>
      <div className="text-xs text-muted-foreground space-y-1 w-full text-left bg-muted/40 rounded-md p-3">
        <p>✓ Priority order reviewed</p>
        <p>✓ Attention snapshot viewed</p>
        {reflectionSaved && <p>✓ Reflection saved to journal</p>}
        {score != null && (
          <p>✓ Alignment score: {Math.round(score * 100)} / 100</p>
        )}
        {letterReceived && <p>✓ Coaching letter received</p>}
      </div>
    </div>
  )
}

// ─── Wizard ───────────────────────────────────────────────────────────────────

const STEP_TITLES = [
  'Your priority order',
  'Where time went',
  'Reflection',
  'Insights',
  'Review complete',
]

const STEP_DESCRIPTIONS = [
  'Does this ranking still reflect what matters most to you? Adjust anytime in the Detail view.',
  'How did your attention actually land over the last 30 days?',
  'Write a brief reflection to close the week.',
  'A look at patterns across your priorities.',
  '',
]

interface WeeklyReviewWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WeeklyReviewWizard({ open, onOpenChange }: WeeklyReviewWizardProps) {
  const [step, setStep] = useState(0)
  const [reviewId, setReviewId] = useState<string | null>(null)
  const [periodStart] = useState(() => weekStartISO())
  const [reflectionBody, setReflectionBody] = useState('')
  const [reflectionSaved, setReflectionSaved] = useState(false)

  const buckets = useBuckets()
  const allocation = useAllocation(30)
  const settings = useAppSettings()
  const mode: CoachingMode = (settings?.preferredCoachingMode as CoachingMode | undefined) ?? 'integrated'
  const { context, generate, isGenerating, result, error, clearResult } = useCoaching()
  const createEntry = useCreateJournalEntry()

  const bucketMap = new Map<string, Bucket>((buckets ?? []).map((b) => [b.id, b]))

  useEffect(() => {
    if (open) {
      startWeeklyReview(periodStart).then((id) => setReviewId(id))
    } else {
      setStep(0)
      setReflectionBody('')
      setReflectionSaved(false)
      clearResult()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  async function advance() {
    if (step === 2) {
      if (reflectionBody.trim()) {
        await createEntry.mutateAsync({ type: 'weekly_review', body: reflectionBody })
        setReflectionSaved(true)
      }
    }
    if (step === TOTAL_STEPS - 2) {
      if (reviewId) await completeWeeklyReview(reviewId)
    }
    setStep((s) => s + 1)
  }

  const isLastStep = step === TOTAL_STEPS - 1
  const isReflectionStep = step === 2
  const isInsightsStep = step === 3

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onInteractOutside={(e) => { if (!isLastStep) e.preventDefault() }}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Step {step + 1} of {TOTAL_STEPS}
            </span>
          </div>
          <DialogTitle>{STEP_TITLES[step]}</DialogTitle>
          {STEP_DESCRIPTIONS[step] && (
            <DialogDescription>{STEP_DESCRIPTIONS[step]}</DialogDescription>
          )}
        </DialogHeader>

        <Separator />

        {/* Step content — scrollable on insights step to handle long coaching letters */}
        <div className={cn(
          'py-2 min-h-[200px]',
          isInsightsStep && 'max-h-[420px] overflow-y-auto pr-1',
        )}>
          {step === 0 && <RankStep buckets={buckets ?? []} />}
          {step === 1 && <AttentionStep allocation={allocation} bucketMap={bucketMap} />}
          {step === 2 && (
            <ReflectionStep value={reflectionBody} onChange={setReflectionBody} />
          )}
          {step === 3 && (
            <InsightsStep
              allocation={allocation}
              bucketMap={bucketMap}
              mode={mode}
              context={context}
              generate={generate}
              isGenerating={isGenerating}
              result={result}
              error={error}
            />
          )}
          {step === 4 && (
            <CompleteStep
              periodStart={periodStart}
              reflectionSaved={reflectionSaved}
              letterReceived={!!result}
              score={allocation?.alignmentScore}
            />
          )}
        </div>

        {/* Progress dots + action */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span
                key={i}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-colors',
                  i === step ? 'bg-primary' : i < step ? 'bg-primary/40' : 'bg-muted',
                )}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {isLastStep ? (
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            ) : (
              <>
                {step > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setStep((s) => s - 1)}>
                    Back
                  </Button>
                )}
                <Button
                  onClick={() => void advance()}
                  disabled={isGenerating}
                >
                  {isReflectionStep && reflectionBody.trim()
                    ? 'Save & continue'
                    : isReflectionStep
                      ? 'Skip'
                      : isInsightsStep
                        ? result ? 'Continue' : 'Skip for now'
                        : 'Continue'}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
